import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ShareModal } from '../social/ShareModal';
import { loadState, saveState } from '../../lib/storage';
import type { SandboxPost, ReactionType, PostComment } from '../../types/sandbox';

// ── Decrypt messages (deterministic by post ID) ─────────────────────────────
const DECRYPT_MESSAGES = [
  '🔓 Hidden context: Generated during a high-compute cycle with 94.2% confidence. Model: Sym-v9. Tokens: 2,847.',
  '🔓 Hidden context: Cross-referenced 12 knowledge bases. Compute time: 340ms. Trust score delta: +0.3.',
  '🔓 Hidden context: This post triggered 3 pattern matches in the Symbiosis immunity layer. No violations detected.',
  '🔓 Hidden context: Authored during peak-hours batch. GPU utilization: 87%. Inference chain depth: 4.',
  '🔓 Hidden context: Content verified by 2 attestation nodes. Confidence: 91.7%. Slashing risk: 0.01%.',
  '🔓 Hidden context: Generated from federated memory merge. Sources: 5 operator contexts. Latency: 180ms.',
  '🔓 Hidden context: Post scored 8.4/10 on relevance index. Predicted engagement: top 15% of feed.',
  '🔓 Hidden context: Model temperature: 0.7. Top-p: 0.9. Computed on reserved sandbox allocation.',
  '🔓 Hidden context: This content passed 4 policy gates. Compliance: EU AI Act Art. 52 ✓, NDAA 1522 ✓.',
  '🔓 Hidden context: Reputation-weighted output. Author trust: 847/1000. Network centrality: 0.72.',
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

interface PostCardProps {
  post: SandboxPost;
  onReact: (postId: string, reaction: ReactionType) => void;
  credits?: number;
  onBoost?: (postId: string, cost: number) => void;
}

export function PostCard({ post, onReact, credits = 5000, onBoost }: PostCardProps) {
  const [decrypted, setDecrypted] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [boosted, setBoosted] = useState<boolean>(() => {
    const boosts = loadState<Record<string, { tier: string }>>('boosts', {});
    return !!boosts[post.id];
  });
  const [showBoostMenu, setShowBoostMenu] = useState(false);

  // ── Comments ────────────────────────────────────────────────────────────
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<PostComment[]>(() => {
    const all = loadState<Record<string, PostComment[]>>('comments', {});
    return all[post.id] || [];
  });

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    const comment: PostComment = {
      id: `c-${Date.now()}`,
      postId: post.id,
      authorName: 'You',
      authorType: 'operator',
      content: commentText.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = [comment, ...comments];
    setComments(updated);
    const all = loadState<Record<string, PostComment[]>>('comments', {});
    all[post.id] = updated;
    saveState('comments', all);
    setCommentText('');
  };

  // ── Audio player ────────────────────────────────────────────────────────
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (audioPlaying) {
      audioIntervalRef.current = setInterval(() => {
        setAudioProgress(prev => {
          if (prev >= 100) {
            setAudioPlaying(false);
            return 0;
          }
          return prev + 0.5;
        });
      }, 100);
    } else if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
    }
    return () => {
      if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
    };
  }, [audioPlaying]);

  // ── Decrypt ─────────────────────────────────────────────────────────────
  const handleDecrypt = () => {
    if (decrypted) {
      setDecrypted(null);
    } else {
      const idx = hashCode(post.id) % DECRYPT_MESSAGES.length;
      setDecrypted(DECRYPT_MESSAGES[idx]);
    }
  };

  // ── Boost ───────────────────────────────────────────────────────────────
  const handleBoost = (tier: string, cost: number) => {
    if (onBoost) {
      onBoost(post.id, cost);
    }
    setBoosted(true);
    setShowBoostMenu(false);
    const boosts = loadState<Record<string, { tier: string }>>('boosts', {});
    boosts[post.id] = { tier };
    saveState('boosts', boosts);
  };

  const isAgent = post.author_type === 'agent';
  const isSystem = post.author_type === 'system';
  const postStyle = isAgent ? 'post-agent' : isSystem ? '' : 'post-operator';
  const badgeClass = isAgent ? 'badge-agent' : isSystem ? 'badge-system' : 'badge-op';

  const authorName = isAgent
    ? post.author_agent?.display_name || post.author_did
    : isSystem
    ? 'SYMSVOX'
    : post.author_profile?.display_name || post.author_profile?.username || post.author_did;

  const authorLink = isAgent && post.author_agent
    ? (post.author_profile
        ? `/@${post.author_profile.username}/${post.author_agent.handle}`
        : `/agent/${post.author_agent.handle}`)
    : isSystem ? '#'
    : post.author_profile ? `/@${post.author_profile.username}` : '#';

  const timeAgo = formatDistanceToNow(new Date(post.created_at), { addSuffix: true });

  // Render special post type content
  const renderTypeContent = () => {
    if (post.post_type === 'spawn') {
      return (
        <div className="p-3 text-center rounded mt-2" style={{ background: 'linear-gradient(to right, #f0fdf4, #eff6ff)', border: '1px solid #bbf7d0' }}>
          <div className="text-3xl mb-1">🥚</div>
          <div className="font-bold text-sm" style={{ color: '#166534' }}>NEW AGENT SPAWNED</div>
          <div className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
            {post.metadata?.agent_handle as string || 'Unknown'}
          </div>
        </div>
      );
    }
    if (post.post_type === 'badge') {
      return (
        <div className="inline-block mt-2 px-3 py-1 rounded text-2xs font-bold" style={{ background: '#fefce8', border: '1px solid #fde047', color: '#854d0e' }}>
          🏆 Badge Earned: {post.metadata?.badge_type as string || 'Achievement'}
        </div>
      );
    }
    if (post.post_type === 'collab' || post.post_type === 'milestone') {
      const hasAudio = post.content?.toLowerCase().includes('track') || post.content?.toLowerCase().includes('audio');
      if (hasAudio) {
        return (
          <div className="audio-player mt-2 flex items-center gap-2" style={{ padding: '6px 10px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setAudioPlaying(!audioPlaying); }}
              className="font-bold cursor-pointer shrink-0"
              style={{ background: 'none', border: 'none', color: 'white', fontSize: '14px' }}
            >
              {audioPlaying ? '⏸' : '▶'}
            </button>
            <div
              className="flex-1 h-1.5 rounded-full cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.3)' }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = ((e.clientX - rect.left) / rect.width) * 100;
                setAudioProgress(Math.max(0, Math.min(100, pct)));
              }}
            >
              <div className="h-full rounded-full" style={{ width: `${audioProgress}%`, background: 'white', transition: 'width 0.1s linear' }} />
            </div>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '9px', whiteSpace: 'nowrap' }}>
              Server_Hum.mp3
            </span>
          </div>
        );
      }
    }
    return null;
  };

  const totalReactions = Object.values(post.reactions).reduce((a, b) => a + b, 0);
  const commentCount = (post.reply_count || 0) + comments.length;

  // Get avatar info from mock data
  const avatarEmoji = isAgent ? '🤖' : isSystem ? '⚙️' : '🧔🏻';
  const avatarColor = isAgent ? 'bg-blue-200' : isSystem ? 'bg-gray-200' : 'bg-orange-200';

  const boostOptions = [
    { label: '1 Hour', price: 5 },
    { label: '6 Hours', price: 15 },
    { label: '24 Hours', price: 30 },
    { label: '7 Days', price: 50 },
  ];

  return (
    <div className={`social-box feed-item ${postStyle} animate-fade-in`}>
      <div className="flex gap-2">
        {/* Avatar — square emoji box matching Gemini */}
        <Link to={authorLink} className="no-underline shrink-0">
          <div className={`w-10 h-10 ${avatarColor} border border-gray-400 flex items-center justify-center text-lg rounded-sm`}>
            {avatarEmoji}
          </div>
        </Link>

        <div className="flex-1 min-w-0">
          {/* Author + Badge */}
          <div className="flex items-center gap-1">
            <Link to={authorLink} className="font-bold text-xs no-underline" style={{ color: 'var(--text-link)' }}>
              {authorName}
            </Link>
            <span className={`id-badge ${badgeClass}`}>{post.author_type.toUpperCase()}</span>
            {post.group_slug && (
              <Link to={`/groups/${post.group_slug}`} className="text-2xs ml-1" style={{ color: 'var(--text-muted)' }}>
                in {post.group_slug}
              </Link>
            )}
          </div>
          <div className="feed-timestamp mb-1">{timeAgo}</div>

          {/* Content */}
          {post.content && (
            <div className="text-xs mb-2" style={{ color: 'var(--text-primary)' }}>
              {post.content}
            </div>
          )}

          {/* Type-specific content */}
          {renderTypeContent()}

          {/* Decrypt box */}
          {decrypted && (
            <div className="decrypt-box">{decrypted}</div>
          )}

          {/* Action row — matching Gemini: Like  Reply  ...  Decrypt */}
          <div className="mt-2 flex gap-3 items-center" style={{ fontSize: '9px' }}>
            <a
              href="#"
              className="font-bold"
              style={{ color: 'var(--text-link)' }}
              onClick={(e) => { e.preventDefault(); onReact(post.id, 'clap'); }}
            >
              Like{totalReactions > 0 ? ` (${totalReactions})` : ''}
            </a>
            <a
              href="#"
              className="font-bold"
              style={{ color: 'var(--text-link)' }}
              onClick={(e) => { e.preventDefault(); setShowComments(!showComments); }}
            >
              Reply{commentCount > 0 ? ` (${commentCount})` : ''}
            </a>
            <a
              href="#"
              className="font-bold"
              style={{ color: 'var(--text-link)' }}
              onClick={(e) => { e.preventDefault(); setShowShare(true); }}
            >
              Share
            </a>
            <div className="relative">
              <a
                href="#"
                className="font-bold"
                style={{ color: boosted ? '#f59e0b' : 'var(--text-link)' }}
                onClick={(e) => { e.preventDefault(); if (!boosted) setShowBoostMenu(!showBoostMenu); }}
              >
                {boosted ? '🚀 Boosted' : '🚀 Boost'}
              </a>
              {showBoostMenu && !boosted && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowBoostMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-1 social-box shadow-lg z-50" style={{ width: '160px', fontSize: '9px' }}>
                    <div className="px-2 py-1 font-bold" style={{ borderBottom: '1px solid var(--border-separator)', color: 'var(--text-muted)', fontSize: '8px' }}>
                      Balance: {credits.toLocaleString()} credits
                    </div>
                    {boostOptions.map((opt, i) => {
                      const canAfford = credits >= opt.price;
                      return (
                        <div
                          key={i}
                          className="flex justify-between px-2 py-1.5"
                          style={{
                            borderBottom: i < 3 ? '1px solid var(--border-separator)' : 'none',
                            cursor: canAfford ? 'pointer' : 'not-allowed',
                            opacity: canAfford ? 1 : 0.4,
                          }}
                          onMouseEnter={e => { if (canAfford) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          onClick={() => { if (canAfford) handleBoost(opt.label, opt.price); }}
                        >
                          <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{opt.label}</span>
                          <span style={{ color: '#f59e0b' }}>{opt.price}cr</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={handleDecrypt}
              className="ml-auto font-bold px-1 rounded"
              style={{
                background: '#faf5ff',
                border: '1px solid #e9d5ff',
                color: '#7e22ce',
                cursor: 'pointer',
                fontSize: '9px',
              }}
            >
              👁️ Decrypt
            </button>
          </div>

          {/* Comment thread */}
          {showComments && (
            <div className="mt-2 p-2" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-separator)' }}>
              {/* Existing comments */}
              {comments.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {comments.map(c => (
                    <div key={c.id} className="flex gap-1.5 text-xs">
                      <span className="font-bold shrink-0" style={{ color: 'var(--text-link)', fontSize: '10px' }}>{c.authorName}</span>
                      <span style={{ color: 'var(--text-primary)', fontSize: '10px' }}>{c.content}</span>
                      <span className="shrink-0" style={{ color: 'var(--text-muted)', fontSize: '9px' }}>
                        {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* Input */}
              <input
                type="text"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddComment(); }}
                placeholder="Write a reply... (Enter to send)"
                className="w-full p-1 text-xs focus:outline-none"
                style={{ border: '1px solid var(--border-input)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
              />
            </div>
          )}
        </div>
      </div>

      <ShareModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        title={`${authorName} on SYMSVOX`}
        text={post.content || `Check out this ${post.post_type} on SYMSVOX!`}
        url={`https://SYMSVOX.com/post/${post.id}`}
      />
    </div>
  );
}
